/**
 * Runs once at bootstrap (before ReactDOM.render / before HashRouter mounts).
 *
 * Ad platforms (Meta fbclid, Google gclid, etc.) append click-IDs as real
 * query params BEFORE the #, e.g.:
 *   https://panel.nipponpolls.com/?fbclid=XXXX#/auth/signup?loginRewardType=facebook
 *
 * HashRouter only reads window.location.hash, so pre-hash params are invisible
 * to the router AND to hash-internal URLSearchParams. This utility merges them
 * into the hash fragment so the entire app sees a clean, unified URL:
 *   https://panel.nipponpolls.com/#/auth/signup?loginRewardType=facebook&fbclid=XXXX
 *
 * Rules:
 *   - Hash-internal params WIN on name collision (pre-hash params never override).
 *   - After merge the real query string is removed (nothing left before the #).
 *   - Runs only when window.location.search is non-empty — no-op otherwise.
 *   - Uses history.replaceState so it doesn't create a back-stack entry.
 *
 * _fbc cookie:
 *   Meta's pixel normally auto-sets _fbc by scanning the real query string for
 *   fbclid at load time. Because we remove fbclid from the query string before
 *   fbevents.js loads, we set it manually here (latest click always wins).
 *   No explicit domain attribute — browser scopes it to the current host.
 */
export function normalizeTrackingParams(): void {
  const search = window.location.search; // e.g. "?fbclid=XXXX&utm_source=fb"
  if (!search) return;

  const preHashParams = new URLSearchParams(search);

  // Set _fbc cookie before mutating the URL, while fbclid is still readable.
  // Latest click always wins — overwrite any existing _fbc from a prior visit.
  const fbclid = preHashParams.get('fbclid');
  if (fbclid) {
    document.cookie = `_fbc=fb.1.${Date.now()}.${fbclid}; path=/; max-age=${90 * 24 * 60 * 60}`;
  }

  // Parse the current hash: split path from its own query string.
  // window.location.hash is the full "#..." string including the leading "#".
  const rawHash = window.location.hash; // e.g. "#/auth/signup?loginRewardType=facebook"
  const hashInner = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
  const qMark = hashInner.indexOf('?');
  const hashPath = qMark === -1 ? hashInner : hashInner.slice(0, qMark);
  const hashSearch = qMark === -1 ? '' : hashInner.slice(qMark + 1);

  const merged = new URLSearchParams(hashSearch); // start with hash-internal params
  preHashParams.forEach((value, key) => {
    if (!merged.has(key)) {        // hash params win on conflict
      merged.set(key, value);
    }
  });

  const newHash = merged.toString()
    ? `#${hashPath}?${merged.toString()}`
    : `#${hashPath}`;

  // origin + pathname removes the query string; newHash adds the merged fragment.
  window.history.replaceState(null, '', window.location.origin + window.location.pathname + newHash);
}
